import { inject, Injectable } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { OrganisationDataFormService } from './organisation-data-form.service';
import { SenderIdsFormService } from './sender-ids-form.service';
import { SenderIdsOpasFormService } from './sender-ids-opas-form.service';
import { SignUpV2FormGroup } from '../interfaces/forms/sign-up-form-v2.interface';
import { RegisterFormWithSenderIdsRequest } from '../interfaces/forms/register-form.interface';
import { startWith, tap } from 'rxjs';
import { CreateSenderIdItem } from '../interfaces/create-sender-id-item.interface';
import { ThirdPartyAssignmentItem } from '../interfaces/third-party-assignement-item.interface';
import { SignupDeclarationFormService } from './signup-declaration-form.service';
import { RecaptchaFormService } from './recaptcha-form.service';

@Injectable({
  providedIn: 'root',
})
export class SignupFormService {
  organisationDataFormService = inject(OrganisationDataFormService);
  senderIdsFormService = inject(SenderIdsFormService);
  senderIdsOpasFormService = inject(SenderIdsOpasFormService);
  signupDeclarationForm = inject(SignupDeclarationFormService);
  recaptchaFormService = inject(RecaptchaFormService);
  fb = inject(FormBuilder);

  formGroup: SignUpV2FormGroup;

  private _thirdParties: ThirdPartyAssignmentItem[];

  public set thirdParties(value: ThirdPartyAssignmentItem[]) {
    this._thirdParties = value;
  }

  public initFormGroup() {
    this.formGroup = this.fb.group({
      steps: this.fb.array([
        this.organisationDataFormService.initFormGroup(),
        this.senderIdsFormService.initFormArray(false),
        this.senderIdsOpasFormService.initFormArray(0),
        this.signupDeclarationForm.initFormGroup(),
        this.recaptchaFormService.initFormGroup(),
      ]),
    });

    this.senderIdsFormService.formArray.valueChanges.pipe(
      startWith(['']),
      tap((value) => {
        const hasNoValue = value.length === 1 && value[0].trim().length === 0;
        if (hasNoValue) {
          this.senderIdsOpasFormService.disableValidation();
        } else {
          this.senderIdsOpasFormService.enableValidation(0);
        }
      }),
    ).subscribe();
  }

  public toRegisterFormWithSenderIdsRequest(selectedThirdParties: string[][]): { data: RegisterFormWithSenderIdsRequest, file?: File } {
    const senderIdNames = this.senderIdsFormService.formArray.value
      .map(senderId => senderId.trim())
      .filter(senderId => senderId.length > 0);

    const opaList = this.senderIdsOpasFormService.formArray.value
      .filter(value => value.selected)
      .map(item => ({ accountId: item.id }));

    const { data, file } = this.organisationDataFormService.toRegisterFormEvent();

    const senderIdList: CreateSenderIdItem[] = senderIdNames.map((text, index) => ({
      senderIdText: text,
      thirdPartyIdList: this._thirdParties.filter(thirdParty => (selectedThirdParties[index] ?? []).includes(thirdParty.organisationName)).map(thirdParty => thirdParty.thirdPartyID),
    }));

    const requestData: RegisterFormWithSenderIdsRequest = {
      ...data,
      recaptcha: this.recaptchaFormService.formGroup.value.recaptcha,
      senderIds: {
        senderIdList,
        opaList,
      },
    };

    return { data: requestData, file };
  }
}
